import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { TrendingUp, Trophy, Timer, UserPlus2 } from "lucide-react";

function MetricPill({ icon: Icon, label, value, sub, tint, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500 text-[10px] uppercase tracking-widest">
        <Icon className="w-3.5 h-3.5" />{label}
      </div>
      <div className={`mt-1 text-2xl font-bold ${tint || "text-[#0D1B2A]"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function ConversionFunnel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/crm/leads/funnel")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6 text-sm text-slate-500">Loading funnel…</CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const funnel = data.funnel || [];
  const maxCum = Math.max(1, ...funnel.map((f) => f.cumulative));

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="conversion-funnel">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Conversion analytics</p>
            <h2 className="text-xl font-semibold tracking-tight text-[#0D1B2A]">Sales funnel</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Historical reach + drop-off across the sales pipeline.
            </p>
          </div>
          <Link to="/crm" className="text-[#1A7F6E] text-sm hover:underline font-medium">
            Open pipeline →
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MetricPill
            icon={TrendingUp}
            label="New → proposal"
            value={`${data.overall?.conversion_rate ?? 0}%`}
            sub={`${data.overall?.reached_engaged ?? 0} of ${data.overall?.reached_new ?? 0} leads`}
            tint="text-[#1A7F6E]"
            testid="funnel-metric-conversion"
          />
          <MetricPill
            icon={Trophy}
            label="Lost rate"
            value={`${data.win_rate?.lost && data.total_leads ? Math.round(data.win_rate.lost / data.total_leads * 100) : 0}%`}
            sub={`${data.win_rate?.lost ?? 0} lost`}
            tint="text-[#0D1B2A]"
            testid="funnel-metric-winrate"
          />
          <MetricPill
            icon={Timer}
            label="Avg time to proposal"
            value={data.overall?.avg_days_to_engaged != null
              ? `${data.overall.avg_days_to_engaged}d`
              : "—"}
            sub="From lead creation"
            testid="funnel-metric-avg-days"
          />
          <MetricPill
            icon={UserPlus2}
            label={`New (last ${data.window?.days ?? 90}d)`}
            value={data.window?.new_leads ?? 0}
            sub="Fresh leads"
            testid="funnel-metric-new-window"
          />
        </div>

        <div className="space-y-2">
          {funnel.map((row, idx) => {
            const widthPct = Math.max(3, Math.round((row.cumulative / maxCum) * 100));
            const conv = row.conversion_from_prev;
            return (
              <div
                key={row.key}
                className="grid grid-cols-[160px_1fr_auto] items-center gap-3"
                data-testid={`funnel-row-${row.key}`}
              >
                <div className="text-sm">
                  <div className="font-medium text-[#0D1B2A]">{row.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {row.current} currently · {row.cumulative} ever
                  </div>
                </div>
                <div className="relative h-9 bg-[#E6F4F1]/60 rounded-md overflow-hidden">
                  <div
                    className="h-full bg-[#1A7F6E] rounded-md flex items-center justify-end pr-2 text-xs font-semibold text-white transition-[width] duration-500"
                    style={{ width: `${widthPct}%` }}
                  >
                    {row.cumulative}
                  </div>
                </div>
                <div className="text-right min-w-[76px]">
                  {idx === 0 ? (
                    <span className="text-xs text-slate-400">entry</span>
                  ) : (
                    <>
                      <div className={`text-sm font-semibold ${
                        conv == null ? "text-slate-400"
                          : conv >= 75 ? "text-[#1A7F6E]"
                          : conv >= 40 ? "text-amber-600"
                          : "text-rose-600"
                      }`}>
                        {conv == null ? "—" : `${conv}%`}
                      </div>
                      {row.drop_off != null && row.drop_off > 0 && (
                        <div className="text-[10px] text-rose-500">−{row.drop_off}</div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
